import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Tag, Typography, Table, Input, Space, Dropdown, Button, App, Tooltip } from 'antd';
import { MoreOutlined, EyeOutlined, EditOutlined, LockOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { listClients, updateClientStatus, deleteClient } from '../../services/clients';

const { Title } = Typography;
const { useApp } = App;

const ClientsList = () => {
  const navigate = useNavigate();
  const { message, modal } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [sorter, setSorter] = useState({});
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async (page, pageSize, srt, q) => {
    setLoading(true);
    try {
      const { items, total } = await listClients({
        search: q || undefined,
        page: page || pagination.current,
        pageSize: pageSize || pagination.pageSize,
        sortField: srt?.field,
        sortOrder: srt?.order,
      });
      setData(items || []);
      setPagination(prev => ({ 
        ...prev, 
        current: page || prev.current, 
        pageSize: pageSize || prev.pageSize, 
        total: total || 0 
      }));
    } catch (err) {
      console.error(err);
      message.error('No se pudo cargar la lista de clientes.');
    } finally {
      setLoading(false);
    }
  }, [pagination, message]);

  useEffect(() => {
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (pag, filters, srt) => {
    setSorter(srt || {});
    fetchData(pag.current, pag.pageSize, srt, search);
  };

  const onSearch = (value) => {
    setSearch(value || '');
    // al buscar, reinicia a página 1
    fetchData(1, pagination.pageSize, sorter, value || '');
  };

  // Helper para obtener el nombre completo
  const fullName = (r) =>
    [r.first_name ?? r.name, r.last_name ?? r.lastname, r.last_name2 ?? r.lastname2]
      .filter(Boolean).join(' ');

  // Función para manejar las acciones del menú
  const handleMenuAction = (action, record) => {
    switch (action) {
      case 'view':
        navigate(`/clients/view/${record.id}`);
        break;
      case 'edit':
        navigate(`/clients/${record.id}/edit`);
        break;
      case 'toggle':
        const willActivate = !record.is_active;
        modal.confirm({
          title: willActivate ? '¿Activar cliente?' : '¿Desactivar cliente?',
          content: `${fullName(record)}`,
          okText: willActivate ? 'Activar' : 'Desactivar',
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await updateClientStatus(record.id, willActivate);
              message.success(willActivate ? 'Cliente activado' : 'Cliente desactivado');
              fetchData(pagination.current, pagination.pageSize, sorter, search);
            } catch {
              message.error('No se pudo actualizar el estatus');
            }
          },
        });
        break;
      case 'delete':
        modal.confirm({
          title: '¿Eliminar cliente?',
          content: `${fullName(record)}`,
          okText: 'Eliminar',
          okButtonProps: { danger: true },
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await deleteClient(record.id);
              message.success('Cliente eliminado');
              
              const nextPage = (data.length === 1 && pagination.current > 1) 
                ? pagination.current - 1 
                : pagination.current;
              setPagination(prev => ({ ...prev, current: nextPage }));
              fetchData(nextPage, pagination.pageSize, sorter, search);
            } catch {
              message.error('No se pudo eliminar el cliente');
            }
          },
        });
        break;
      default:
        break;
    }
  };

  const getActionsMenu = (record) => {
    const menuItems = [
      {
        key: 'view',
        icon: <EyeOutlined />,
        label: 'Ver cliente',
      },
      {
        key: 'edit',
        icon: <EditOutlined />,
        label: 'Editar cliente',
      },
      {
        key: 'toggle',
        icon: <LockOutlined />,
        label: record.is_active ? 'Desactivar' : 'Activar',
      },
      {
        type: 'divider',
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Eliminar cliente',
        danger: true,
      },
    ];

    return {
      items: menuItems,
      onClick: ({ key }) => handleMenuAction(key, record),
    };
  };

  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'first_name',
      key: 'first_name',
      //render: (_, r) => r.first_name ?? r.name ?? '-',
      sorter: true,
    },
    {
      title: 'Apellidos',
      dataIndex: 'last_name',
      key: 'last_name',
      render: (_, r) => [r.last_name, r.second_last_name].filter(Boolean).join(' '),
      sorter: true,
    },
    { 
      title: 'Email', 
      dataIndex: 'email', 
      key: 'email',
      sorter: true,
    },
    {
      title: 'Estatus',
      dataIndex: 'is_active',
      key: 'status',
      render: (isActive) => (
        <Tag className={isActive ? 'status-positive' : undefined}>
          {isActive ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
      // filters: [
      //   { text: 'Activo', value: '1' },
      //   { text: 'Inactivo', value: '0' },
      // ],
      // onFilter: (value, record) => String(record.is_active ? 1 : 0) === String(value),
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      width: 100,
      render: (_, record) => (
        <Dropdown menu={getActionsMenu(record)} trigger={['click']} placement="bottomRight">
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
            style={{ fontSize: '16px' }}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={<Title level={3} style={{ marginBottom: 0 }}>Clientes</Title>}
        extra={
          <Tooltip title="Agregar cliente">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              aria-label="Agregar cliente"
              onClick={() => navigate('/clients/add')}
            />
          </Tooltip>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input.Search
            placeholder="Buscar por nombre o email"
            allowClear
            onSearch={onSearch}
            style={{ maxWidth: 360 }}
          />
          <Table
            rowKey={(r) => r.id ?? r._id}
            columns={columns}
            dataSource={data}
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
            }}
            onChange={onChange}
          />
        </Space>
      </Card>
    </div>
  );
};

export default ClientsList;
